// @ts-nocheck

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CodeIcon from '@mui/icons-material/Code';
import GroupsIcon from '@mui/icons-material/Groups';
import GetAppIcon from '@mui/icons-material/GetApp';
import DownloadIcon from '@mui/icons-material/Download';
import AssessmentIcon from '@mui/icons-material/Assessment';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Gauge } from '@mui/x-charts/Gauge';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { useDebouncedCallback } from 'use-debounce';

import CompanyLinks from '../../components/CompanyLinks/CompanyLinks';
import MergeOrganizations from '../../components/MergeOrganizations/MergeOrganizations';
import PageHeader from '../../components/PageHeader';
import SearchComponent from '../../components/Search/Search';
import TagsBox from '../../components/TagsBox/TagsBox';
import { FLAG_URL } from '../../contants';
import { useNotifications } from '../../contexts/NotificationsContext';
import {
  CompanyAssesmentEdge,
  CompanyAssessmentType,
  useCompetitionCpcKeywordsLazyQuery,
  useCustomerIndustryGrowthRatesLazyQuery,
  useDealAssessmentFileUrlLazyQuery,
  useEmployeeStaticsLazyQuery,
  useGetCompanyCoreKeywordsLazyQuery,
  useGetCompanyForDfmLazyQuery,
  useGetLatestCrawlRunLazyQuery,
  useGetProductCategoriesForCompanyLazyQuery,
  useGetProgrammingLanguagesLazyQuery,
  useProductCategoryTrendsLazyQuery,
  useGetSoftwareFirmsLazyQuery,
  useKpiCalculationStatusQuery,
  usePrecalculatedKpisLazyQuery,
  useSoftwareCategoryGrowthRatesLazyQuery,
  useSpecialCpcKeywordsLazyQuery,
  useTopCpcKeywordLazyQuery,
} from '../../gql/generated/graphql';
import { getFirstNode, getNodes } from '../../gql/helpers';
import { extractKeywords } from '../../helpers/helpers';
import useResponsive from '../../hooks/useResponsive';
import { paths } from '../../routes/paths';
import { ValidationSteps } from '../ValidationPage/types';

import CompanyHeader from './components/CompanyHeader/CompanyHeader';
import CompanyOverview from './components/CompanyOverview/CompanyOverview';
import CustomPaper from './components/CustomPaper/CustomPaper';
import EditButton from './components/EditButton/EditButton';
import KPISection from './components/KPISection/KPISection';
import CallAssessmentSection from './components/CallAssessmentSection/CallAssessmentSection';
import CrawlProgressSection from './components/CrawlProgressSection/CrawlProgressSection';
import ModernSection from './components/ModernSection/ModernSection';
import {
  cpcCompetitionKeywordsColumns,
  customerIndustryGrowthColumns,
  employeStaticsColumns,
  precalculatedFinanceKpisColumns,
  precalculatedOnlineKpisColumns,
  precalculatedTechKpisColumns,
  softwareGrowthRatesColumns,
} from './helpers';
import { transformPrecalculatedKPIs, createAdditionalKPIs } from './utils/kpiDefinitions';
import { useKPIBenchmarks } from '../../hooks/useKPIBenchmarks';

import styles from './styles';

const DFMPage = () => {
  const navigate = useNavigate();
  const { notifications } = useNotifications();
  const { t } = useTranslation();
  const { sm } = useResponsive();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [searchParams, setSearchParams] = useSearchParams();
  const [options, setOptions] = useState<CompanyAssesmentEdge[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [company, setCompany] = useState<CompanyAssessmentType | null>(null);
  const [isGeneratingFile, setIsGeneratingFile] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);

  // Fetch real KPI benchmarks from backend
  const { benchmarks: realBenchmarks, loading: benchmarksLoading } = useKPIBenchmarks();

  const companyParamsId = searchParams.get('companyId');

  const [getSoftwareGrowthRates, { data: softwareGrowthRates, loading: softwareGrowthRatesLoading }] =
    useSoftwareCategoryGrowthRatesLazyQuery();
  const [getCompetitionCpcKeywords, { data: competitionCpcKeywords, loading: competitionCpcKeywordsLoading }] =
    useCompetitionCpcKeywordsLazyQuery();
  const [getProgrammingLanguages, { data: programmingLanguages, loading: getProgrammingLanguagesLoading }] =
    useGetProgrammingLanguagesLazyQuery();
  const [getEmployeeStatics, { data: employeeStatics, loading: employeeStaticsLoading }] =
    useEmployeeStaticsLazyQuery();
  const [
    getProductCategoriesForCompany,
    { data: productCategoriesForCompany, loading: productCategoriesForCompanyLoading },
  ] = useGetProductCategoriesForCompanyLazyQuery();
  const [
    getCustomerIndustryGrowthRates,
    { data: customerIndustryGrowthRates, loading: customerIndustryGrowthRatesLoading },
  ] = useCustomerIndustryGrowthRatesLazyQuery();
  const [getTopCpcKeywords, { data: topCpcKeywords, loading: topCpcKeywordsLoading }] = useTopCpcKeywordLazyQuery();
  const [getSpecialCpcKeywords, { data: specialCpcKeywords, loading: specialCpcKeywordsLoading }] =
    useSpecialCpcKeywordsLazyQuery();
  const [getCoreKeywords, { data: coreKeywordsData, loading: coreKeywordsLoading }] =
    useGetCompanyCoreKeywordsLazyQuery();
  const [getCompanyForDFM, { data: companyForDFM, loading: companyForDFMLoading }] = useGetCompanyForDfmLazyQuery();
  const [getLatestCrawlRun, { data: latestCrawlRun, loading: latestCrawlRunLoading }] = useGetLatestCrawlRunLazyQuery();
  const [getPrecalculatedKpis, { data: precalculatedKpis, loading: precalculatedKpisLoading }] =
    usePrecalculatedKpisLazyQuery();
  const [getDealAssessmentFileUrl, { data: dealAssessmentFileUrl, refetch: dealAssessmentFileUrlRefetch }] =
    useDealAssessmentFileUrlLazyQuery({
      fetchPolicy: 'no-cache',
      onCompleted: data => {
        const dealAssessmentFile = data.getCompany?.edges?.[0]?.node?.dealAssessmentFileUrl;
        if (!dealAssessmentFile?.downloadUrl && dealAssessmentFile?.generationTriggered) {
          setIsGeneratingFile(true);
        } else if (dealAssessmentFile?.downloadUrl) {
          setIsGeneratingFile(false);
        }
      },
    });

  const [getProductCategoryTrends, { data: productCategoryTrendsData }] = useProductCategoryTrendsLazyQuery();

  // Fetch KPI calculation status (includes lastFiscalYear for FY labels)
  const { data: kpiStatusData } = useKpiCalculationStatusQuery({
    variables: { companyId: companyParamsId || company?.dbId },
    skip: !(companyParamsId || company?.dbId),
  });
  const lastFiscalYear = kpiStatusData?.kpiCalculationStatus?.lastFiscalYear;

  // Track whether deferred (collapsed-section) queries have been fired
  const [deferredFired, setDeferredFired] = useState(false);

  useEffect(() => {
    const id = companyParamsId || company?.dbId;
    if (!id) return;

    const criticalOpts = {
      variables: { companyId: id },
      fetchPolicy: 'cache-and-network' as const,
    };
    const noCacheOpts = {
      variables: { companyId: id },
      fetchPolicy: 'no-cache' as const,
    };

    // Critical queries — needed for the initially-visible CompanyOverview + KPI section
    getCompanyForDFM(criticalOpts);
    getPrecalculatedKpis(criticalOpts);
    getCoreKeywords(criticalOpts);
    getProductCategoriesForCompany(criticalOpts);
    getDealAssessmentFileUrl(noCacheOpts);

    // Deferred queries — feed collapsed sections, fire after a short delay
    // to prioritize above-the-fold content
    setDeferredFired(false);
    const timer = setTimeout(() => {
      getSoftwareGrowthRates(criticalOpts);
      getCompetitionCpcKeywords(criticalOpts);
      getProgrammingLanguages(criticalOpts);
      getEmployeeStatics(criticalOpts);
      getCustomerIndustryGrowthRates(criticalOpts);
      getLatestCrawlRun(criticalOpts);
      getProductCategoryTrends(criticalOpts);
      setDeferredFired(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [company, companyParamsId]);

  // Fallback: fetch XOVI keywords only when core keywords query completed with empty results
  const coreKeywordsFinished = !coreKeywordsLoading && coreKeywordsData !== undefined;
  const coreKeywordNodes = coreKeywordsData?.getCompanyCoreKeywords?.edges?.map(e => e?.node) || [];

  useEffect(() => {
    if (!coreKeywordsFinished || coreKeywordNodes.length > 0) return;
    const id = companyParamsId || company?.dbId;
    if (!id) return;
    const options = { variables: { companyId: id }, fetchPolicy: 'no-cache' as const };
    getTopCpcKeywords(options);
    getSpecialCpcKeywords(options);
  }, [coreKeywordsFinished, coreKeywordNodes.length]);

  const softwareGrowthRatesNodes = getNodes(softwareGrowthRates?.softwareCategoryGrowthRates);
  const competitionCpcKeywordsNodes = getNodes(competitionCpcKeywords?.competitionCpcKeywords);
  const programmingLanguagesNodes = getNodes(programmingLanguages?.getProgrammingLanguages);
  const employeeStaticsNodes = getNodes(employeeStatics?.employeeStatics);
  const productCategoriesForCompanyNodes =
    productCategoriesForCompany?.getCompany?.edges?.[0]?.node?.productCategorization?.edges?.map(edge => ({
      dbId: edge.node.softwareCategory?.dbId,
      subCategory1: edge.node.softwareCategory?.subCategory1,
      subCategory2: edge.node.softwareCategory?.subCategory2,
      subCategory3: edge.node.softwareCategory?.subCategory3,
    })) || [];
  const customerIndustryGrowthRatesNodes = getNodes(customerIndustryGrowthRates?.customerIndustryGrowthRates);
  const companyForDFMNode = getFirstNode(companyForDFM?.getCompany);
  const topCpcKeywordsNodes = extractKeywords(topCpcKeywords);
  const specialCpcKeywordsNodes = extractKeywords(specialCpcKeywords);
  const dealAssessmentFile = dealAssessmentFileUrl?.getCompany?.edges?.[0]?.node?.dealAssessmentFileUrl;
  const precalculatedKpisNodes = precalculatedKpis?.companyAssesment?.edges?.[0]?.node?.precalculatedKpis?.edges?.map(
    node => node?.node
  );

  useEffect(() => {
    if (dealAssessmentFile && isGeneratingFile) {
      const { downloadUrl, generationTriggered } = dealAssessmentFile;
      if (downloadUrl) {
        setIsGeneratingFile(false);
      } else if (generationTriggered) {
        const searchString = "{status: 'Finished', task_name: 'generate_deal_assessment_file";

        const finishedGeneratingFile = notifications.some((obj: { message: string; read: boolean; timestamp: Date }) =>
          obj.message.includes(searchString)
        );
        if (finishedGeneratingFile) {
          setIsGeneratingFile(false);
          dealAssessmentFileUrlRefetch();
        }
      }
    }
  }, [isGeneratingFile, notifications, dealAssessmentFile]);

  const debouncedSearch = useDebouncedCallback((value: string) => {
    search({ variables: { searchTerm: value } });
  }, 500);

  const [search, { loading }] = useGetSoftwareFirmsLazyQuery({
    onCompleted: data => {
      setOptions(data.softwareFirms.edges);
    },
  });

  const handleOptionSelect = (_, value: CompanyAssesmentEdge | null) => {
    setCompany(value ? value.node : null);
    setSearchParams({ companyId: value?.node?.dbId?.toString() });
  };

  const handleInputChange = (_, value: string, reason: string) => {
    if (reason === 'clear') {
      handleReset();
      search();
      return;
    }

    setSearchTerm(value);
    if (value) {
      debouncedSearch(value);
    }
  };
  const handleReset = () => {
    setSearchTerm('');
    setOptions([]);
    setCompany(null);
  };

  const handleDownloadFileFromUrl = (url: string) => {
    if (!url) {
      console.error('Download URL is missing');
      if (dealAssessmentFile?.generationTriggered) {
        dealAssessmentFileUrlRefetch();
      }
      return;
    }
    window.open(url, '_blank', 'width=700,height=600,noopener,noreferrer');
  };

  const handleEdit = (step: ValidationSteps) => {
    navigate(`${paths.validation}?companyId=${companyParamsId}&step=${step}`);
  };

  const handleMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDialogOpen = () => {
    setMergeDialogOpen(true);
    setAnchorEl(null);
  };

  const handleCloseDialog = () => {
    setMergeDialogOpen(false);
  };

  const companyName =
    (companyForDFMNode?.shortName !== '<unnamed reference customer>' && companyForDFMNode?.shortName) ||
    companyForDFMNode?.legalName ||
    companyForDFMNode?.uniqueDomain;

  // company_analysis has a unique constraint on company_id — at most one record
  const companyAnalyses = companyForDFMNode?.companyAnalyses?.edges?.[0]?.node;

  const mobilePaperStyle = sm && styles.mobilePaper;

  const employeeConcentrationRatio = precalculatedKpisNodes?.[0]?.employeeConcentrationRatio
    ? `${(precalculatedKpisNodes?.[0]?.employeeConcentrationRatio * 100).toFixed(2)}%`
    : null;

  const callabilityScore = precalculatedKpisNodes?.[0]?.callabilityScore?.toFixed(2);
  const showCallabilityScore = callabilityScore !== null && callabilityScore !== undefined && callabilityScore >= 0;
  const generationTime = dealAssessmentFile?.dateOfFile
    ? format(new Date(dealAssessmentFile?.dateOfFile), 'dd MMMM yyyy HH:mm')
    : null;

  // Memoize KPI computations (called in 3 places in JSX)
  const allKpis = useMemo(
    () => [
      ...transformPrecalculatedKPIs(precalculatedKpisNodes || [], realBenchmarks, lastFiscalYear),
      ...createAdditionalKPIs(precalculatedKpisNodes || [], realBenchmarks, lastFiscalYear),
    ],
    [precalculatedKpisNodes, realBenchmarks, lastFiscalYear]
  );

  // Smart stuck detection: Check if generation is actually stuck (no progress for 5 minutes)
  const progress = dealAssessmentFile?.progress;
  const STUCK_THRESHOLD_SECONDS = 300; // 5 minutes
  const isActuallyStuck =
    dealAssessmentFile?.previousGenerationStillRunning &&
    progress &&
    progress.secondsSinceLastUpdate &&
    progress.secondsSinceLastUpdate > STUCK_THRESHOLD_SECONDS;

  return (
    <Box>
      <PageHeader
        variant="search"
        searchComponent={
          <SearchComponent
            options={options}
            loading={loading}
            searchTerm={searchTerm}
            onInputChange={handleInputChange}
            onOptionSelect={handleOptionSelect}
            onReset={handleReset}
          />
        }
        breadcrumbs={
          companyForDFMNode
            ? [
                { label: 'Home', path: '/' },
                { label: 'Evaluation', path: '/evaluation' },
                { label: companyName || 'Company' },
              ]
            : undefined
        }
        status={
          companyForDFMNode?.isValidated
            ? { label: 'Validated', color: 'success' }
            : companyForDFMNode
              ? { label: 'Not Validated', color: 'warning' }
              : undefined
        }
        actions={undefined}
      />
      {/* Skeleton while primary company data loads */}
      {!companyForDFMNode && companyForDFMLoading && (
        <Box mt={0} display="flex" flexDirection="column">
          <Card sx={{ borderRadius: 0, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box display="flex" gap={2} alignItems="center" mb={2}>
                <Skeleton variant="circular" width={48} height={48} />
                <Box flex={1}>
                  <Skeleton variant="text" width="40%" height={32} />
                  <Skeleton variant="text" width="25%" height={20} />
                </Box>
              </Box>
              <Box display="flex" gap={2} mb={2}>
                <Skeleton variant="rounded" width={100} height={28} />
                <Skeleton variant="rounded" width={80} height={28} />
                <Skeleton variant="rounded" width={120} height={28} />
              </Box>
              <Skeleton variant="rounded" width="100%" height={120} />
            </CardContent>
          </Card>
          <Card sx={{ borderRadius: 0, boxShadow: 'none', border: '1px solid', borderColor: 'divider', mt: 0 }}>
            <CardContent>
              <Skeleton variant="text" width="30%" height={28} sx={{ mb: 1 }} />
              <Box display="flex" gap={2} flexWrap="wrap">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} variant="rounded" width={160} height={100} sx={{ borderRadius: 2 }} />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {companyForDFMNode && (
        <Box mt={0} gap={0} display="flex" flexDirection="column">
          {/* Company Overview Section */}
          <CompanyOverview
            company={companyForDFMNode}
            companyAnalyses={companyAnalyses}
            callabilityScore={callabilityScore}
            ebit={precalculatedKpisNodes?.[0]?.absEbit}
            absSales={precalculatedKpisNodes?.[0]?.absSales}
            salesGrowth={precalculatedKpisNodes?.[0]?.salesGrowth}
            kpiStatusData={kpiStatusData}
            onEdit={handleEdit}
            productCategories={productCategoriesForCompanyNodes}
            categoryTrends={productCategoryTrendsData?.productCategoryTrends}
            keywords={
              coreKeywordNodes.length > 0
                ? coreKeywordNodes
                    .filter(k => k?.keyword)
                    .map(k => ({
                      keyword: k.keyword,
                      cpc: k.cpc,
                      searchVolume: k.searchVolume,
                      competitionIndex: k.competitionIndex,
                      keywordType: k.keywordType,
                    }))
                : [...topCpcKeywordsNodes, ...specialCpcKeywordsNodes]
                    .filter(k => k?.keyword)
                    .map(k => ({ keyword: k.keyword }))
            }
            isLegacyKeywords={
              coreKeywordNodes.length === 0 && (topCpcKeywordsNodes.length > 0 || specialCpcKeywordsNodes.length > 0)
            }
            onDownloadAssessment={() => handleDownloadFileFromUrl(dealAssessmentFile?.downloadUrl)}
            isGeneratingFile={isGeneratingFile}
            generationTime={generationTime}
            hasDataUploaded={!dealAssessmentFile?.noDataUploaded}
            isGenerationStuck={isActuallyStuck}
            progress={progress}
            onMenuOpen={handleMenuOpen}
            anchorEl={anchorEl}
            onMenuClose={handleMenuClose}
            onMergeDialogOpen={handleDialogOpen}
          />

          {/* Enhanced KPI Section */}
          <Box mt={0}>
            <ModernSection
              title={t('assessment.companyPerformanceMetrics')}
              subtitle={t('assessment.companyPerformanceMetricsSubtitle')}
              icon={TrendingUpIcon}
              collapsible
              defaultExpanded
              loading={precalculatedKpisLoading}
              count={precalculatedKpis ? allKpis.length : undefined}
            >
              <KPISection kpis={allKpis} title="" showCategoryFilter={true} defaultCategory="all" compact={sm} />
            </ModernSection>
          </Box>

          {/* Call Assessment Section */}
          {(companyParamsId || company?.dbId) && (
            <Box mt={0}>
              <ModernSection
                title="Call Assessment"
                subtitle="Aggregated survey scores and response history"
                icon={AssessmentIcon}
                collapsible
                defaultExpanded
              >
                <CallAssessmentSection companyId={Number(companyParamsId || company?.dbId)} />
              </ModernSection>
            </Box>
          )}

          {/* KPI Details Section */}
          <ModernSection
            title={t('assessment.kpiDetailsTitle')}
            subtitle={t('assessment.kpiDetailsSubtitle')}
            icon={BusinessIcon}
            collapsible
            defaultExpanded={false}
            loading={
              !deferredFired ||
              softwareGrowthRatesLoading ||
              customerIndustryGrowthRatesLoading ||
              competitionCpcKeywordsLoading ||
              getProgrammingLanguagesLoading ||
              employeeStaticsLoading
            }
            count={
              deferredFired
                ? (softwareGrowthRatesNodes?.length ?? 0) +
                  (customerIndustryGrowthRatesNodes?.length ?? 0) +
                  (competitionCpcKeywordsNodes?.length ?? 0) +
                  (programmingLanguagesNodes?.length ?? 0) +
                  (employeeStaticsNodes?.length ?? 0) +
                  1
                : undefined
            }
          >
            <Box display="flex" flexDirection="column" gap={0}>
              <ModernSection
                title={t('assessment.softwareCategoryGrowth')}
                subtitle="Market growth trends for software categories"
                icon={TrendingUpIcon}
                count={softwareGrowthRatesNodes?.length}
                loading={softwareGrowthRatesLoading}
                collapsible
                defaultExpanded={false}
                headerActions={<EditButton onPress={handleEdit} step={ValidationSteps.VALIDATE_PRODUCT_CATEGORY} />}
              >
                <DataGrid
                  columnVisibilityModel={{
                    interest: !sm,
                    lastInterest: !sm,
                    alltimeInterest: !sm,
                  }}
                  rows={softwareGrowthRatesNodes}
                  getRowId={row => row?.dbId || Math.random()}
                  loading={softwareGrowthRatesLoading}
                  columns={softwareGrowthRatesColumns}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 5,
                      },
                    },
                  }}
                  pageSizeOptions={[5]}
                  disableRowSelectionOnClick
                  sx={{
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${isDark ? '#30363D' : 'rgba(224, 224, 224, 0.5)'}`,
                    },
                  }}
                />
              </ModernSection>

              <ModernSection
                title={t('assessment.customerIndustryGrowthTitle')}
                subtitle="Growth analysis of customer industries"
                icon={BusinessIcon}
                count={customerIndustryGrowthRatesNodes?.length}
                loading={customerIndustryGrowthRatesLoading}
                collapsible
                defaultExpanded={false}
                headerActions={<EditButton onPress={handleEdit} step={ValidationSteps.VALIDATE_CUSTOMERS} />}
              >
                <DataGrid
                  rows={customerIndustryGrowthRatesNodes}
                  columns={customerIndustryGrowthColumns}
                  getRowId={row => row?.dbId}
                  loading={customerIndustryGrowthRatesLoading}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 5,
                      },
                    },
                  }}
                  pageSizeOptions={[5]}
                  disableRowSelectionOnClick
                  sx={{
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${isDark ? '#30363D' : 'rgba(224, 224, 224, 0.5)'}`,
                    },
                  }}
                />
              </ModernSection>

              <ModernSection
                title={t('assessment.competition')}
                subtitle="Competitive landscape and keyword analysis"
                icon={CompareArrowsIcon}
                count={competitionCpcKeywordsNodes?.length}
                loading={competitionCpcKeywordsLoading}
                collapsible
                defaultExpanded={false}
                headerActions={<EditButton onPress={handleEdit} step={ValidationSteps.VALIDATE_KEYWORDS} />}
              >
                <DataGrid
                  rows={competitionCpcKeywordsNodes}
                  columns={cpcCompetitionKeywordsColumns}
                  getRowId={row => row?.dbId}
                  loading={competitionCpcKeywordsLoading}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 5,
                      },
                    },
                  }}
                  pageSizeOptions={[5]}
                  disableRowSelectionOnClick
                  sx={{
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${isDark ? '#30363D' : 'rgba(224, 224, 224, 0.5)'}`,
                    },
                  }}
                />
              </ModernSection>

              <ModernSection
                title={t('assessment.keyTechnologyTitle')}
                subtitle="Technology stack and programming languages"
                icon={CodeIcon}
                count={programmingLanguagesNodes?.length}
                loading={getProgrammingLanguagesLoading}
                collapsible
                defaultExpanded={false}
                headerActions={<EditButton onPress={handleEdit} step={ValidationSteps.VALIDATE_TECHNOLOGY} />}
              >
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {programmingLanguagesNodes?.map(tag => (
                    <Chip
                      key={tag?.dbId}
                      label={tag?.name}
                      color="primary"
                      variant="outlined"
                      sx={{
                        fontWeight: 'medium',
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          color: 'primary.contrastText',
                        },
                      }}
                    />
                  ))}
                </Box>
              </ModernSection>

              <ModernSection
                title={t('assessment.teamQualityTitle')}
                subtitle="Employee statistics and team analysis"
                icon={GroupsIcon}
                count={employeeStaticsNodes?.length}
                loading={employeeStaticsLoading}
                collapsible
                defaultExpanded={false}
              >
                <DataGrid
                  rows={employeeStaticsNodes}
                  columns={employeStaticsColumns}
                  loading={employeeStaticsLoading}
                  getRowId={row => row?.dbId}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 5,
                      },
                    },
                  }}
                  pageSizeOptions={[5]}
                  disableRowSelectionOnClick
                  sx={{
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${isDark ? '#30363D' : 'rgba(224, 224, 224, 0.5)'}`,
                    },
                  }}
                />
              </ModernSection>

              <ModernSection
                title="Assessment Report"
                subtitle="Download comprehensive company assessment"
                icon={GetAppIcon}
                collapsible
                defaultExpanded={false}
              >
                <Box py={2}>
                  <Button
                    onClick={() => handleDownloadFileFromUrl(dealAssessmentFile?.downloadUrl)}
                    disabled={Boolean(dealAssessmentFile?.noDataUploaded) || isGeneratingFile}
                    variant="contained"
                    startIcon={isGeneratingFile ? <CircularProgress size={20} color="primary" /> : <DownloadIcon />}
                  >
                    {isGeneratingFile
                      ? t('assessment.downloadAssessmentLoadingButton')
                      : t('assessment.downloadAssessmentButton')}
                  </Button>

                  {generationTime && (
                    <Typography mt={2} variant="body2" color="text.secondary">
                      {`${t('assessment.dealAssessmentFileGenerationTime')} ${generationTime}`}
                    </Typography>
                  )}

                  {dealAssessmentFile?.noDataUploaded && (
                    <Typography mt={1} variant="caption" color="warning.main">
                      Assessment data is being processed. Please try again later.
                    </Typography>
                  )}
                </Box>
              </ModernSection>
            </Box>
          </ModernSection>

          {/* Crawl Progress Section */}
          <CrawlProgressSection crawlRun={latestCrawlRun?.getLatestCrawlRun} loading={latestCrawlRunLoading} />
        </Box>
      )}
      {companyForDFMNode && (
        <MergeOrganizations open={mergeDialogOpen} company={companyForDFMNode} onClose={handleCloseDialog} />
      )}
    </Box>
  );
};

export default DFMPage;
